import React, { useState, useEffect, useRef } from "react";
import { Box, Chip, IconButton, Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TierDropdown from "./parts/tierDropdown";
import { TiersApi } from "../services/api/api";

function TierDropdownPill(props) {
  const { defaultValue, tierData, token, action, customId } = props;
  const [color, setColor] = useState("#DDDDDD");
  const [tierTitle, setTierTitle] = useState("Free");
  const [fontColor, setFontColor] = useState("#333333");
  const [editTier, setEditTier] = useState(false);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    
    // Initialize the tier display if we have a defaultValue
    if (defaultValue !== undefined && defaultValue !== null) {
      initializeTierDisplay(defaultValue);
    }
    
    return () => {
      isMounted.current = false;
    };
  }, [defaultValue, token, tierData]); // Added tierData as dependency

  const initializeTierDisplay = async (tierId) => {
    try {
      // First try to use provided tierData prop
      if (tierData && Array.isArray(tierData)) {
        const tier = tierData.find(t => t.relId === parseInt(tierId));
        if (tier) {
          initResponse(tier.color, tier.relId, tier.nickname || tier.tierName);
          return;
        }
      }
      
      // Try to get tier data from localStorage as fallback
      const cachedTiers = localStorage.getItem("tierDropdownList");
      if (cachedTiers) {
        const tiers = JSON.parse(cachedTiers);
        const tier = tiers.find(t => t.relId === parseInt(tierId));
        if (tier) {
          initResponse(tier.color, tier.relId, tier.tier);
          return;
        }
      }
      
      // If not in cache, fetch from API as last resort
      if (token) {
        const result = await TiersApi.getTiers(token);
        if (result.success) {
          const tier = result.data.find(t => t.relId === parseInt(tierId));
          if (tier) {
            initResponse(tier.color, tier.relId, tier.tierName);
          }
        }
      }
    } catch (error) {
      console.error("Error initializing tier display:", error);
    }
  };

  const getFontColor = (hexColor) => {
    if (!hexColor) return "#333333";
    const c = hexColor.substring(1); // strip #
    const rgb = parseInt(c, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return luma < 100 ? "#FFFFFF" : "#333333";
  };

  const initResponse = (color, id, name) => {
    if (isMounted.current) {
      setColor(color);
      setTierTitle(name);
      setFontColor(getFontColor(color));
    }
  };

  const toggleSelectMode = () => {
    if (isMounted.current) setEditTier(true);
  };

  const togglePillMode = () => {
    if (isMounted.current) setEditTier(false);
  };

  const setTierData = (color, id, name, tierId) => {
    if (isMounted.current) {
      setColor(color);
      setTierTitle(name);
      setFontColor(getFontColor(color));
    }
    action(color, id, name, tierId);
    togglePillMode();
  };

  return (
    <Box>
      <Collapse in={editTier} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TierDropdown
            token={token}
            action={setTierData}
            defaultValue={defaultValue}
            size="small"
            initResponse={initResponse}
            tierData={tierData}
            customId={customId}
          />
          <IconButton size="small" onClick={togglePillMode}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Collapse>
      <Collapse in={!editTier} timeout="auto" unmountOnExit>
        <Chip
          id={customId ? "tierPill" + customId : undefined}
          label={tierTitle}
          onClick={toggleSelectMode}
          sx={{
            backgroundColor: color,
            color: fontColor,
            cursor: "pointer",
            "&:hover": {
              filter: "brightness(0.9)",
            },
          }}
        />
      </Collapse>
    </Box>
  );
}

export default TierDropdownPill;
